import { emailRegex } from "@lib/constants/generic";
import { FormValidation } from "../types/form";
import { enumContains } from "./generic";

type Type = "array" | "object" | "boolean" | "number" | "string";

export class Validator {
  private validation: FormValidation;
  private value: any;
  private failed: boolean = false;
  private shouldSkip: boolean = false;
  private shouldSkipNext: boolean = false;

  constructor(validation: FormValidation, value: any) {
    this.validation = validation;
    this.value = value;
  }

  public exists(message?: string) {
    const { validation, value } = this;
    if (!this.checkShouldValidate()) return this;

    if (value == null) {
      validation.list.push({ text: message ?? "There's nothing here", type: "error" });
    } else if (value.length != null && value.length === 0) {
      validation.list.push({ text: message ?? "There's nothing here", type: "error" });
    }

    this.setIfFailed();
    return this;
  }

  public is(type: Type, message?: string) {
    const { validation, value } = this;
    if (!this.checkShouldValidate()) return this;

    if (type === "array") {
      if (!Array.isArray(value)) {
        validation.list.push({ text: message ?? "Invalid data", type: "error" });
      }
    } else if (type != null && typeof value !== type) {
      validation.list.push({ text: message ?? "Invalid data", type: "error" });
    }

    this.setIfFailed();
    return this;
  }

  public instanceOf(instance: any, message?: string) {
    const { validation, value } = this;
    if (!this.checkShouldValidate()) return this;

    if (!(value instanceof instance)) {
      validation.list.push({ text: message ?? "Invalid data", type: "error" });
    }

    this.setIfFailed();
    return this;
  }

  public canBe(type: "number" | "json", message?: string) {
    const { validation, value } = this;
    if (!this.checkShouldValidate()) return this;

    switch (type) {
      case "number":
        if (isNaN(Number(value)))
          validation.list.push({ text: message ?? "Must be a number", type: "error" });
        break;
      case "json":
        try {
          JSON.parse(value);
        } catch (e) {
          validation.list.push({ text: message ?? "Must be JSON", type: "error" });
        }
        break;
      default:
        break;
    }

    this.setIfFailed();
    return this;
  }

  public length = {
    greaterThan: (amount: number, inclusive?: boolean, message?: string) => {
      const { validation, value } = this;
      if (!this.checkShouldValidate()) return this;

      const lengthType = typeof value === "string" ? "characters" : "entries";
      const text =
        message != null
          ? message
          : inclusive
          ? `Must be at least ${amount} ${lengthType}`
          : `Must be greater than ${amount} ${lengthType}`;

      if (value.length != null && (inclusive ? value.length < amount : value.length <= amount)) {
        validation.list.push({
          text,
          type: "error",
        });
      }

      this.setIfFailed();
      return this;
    },

    lessThan: (amount: number, inclusive?: boolean, message?: string) => {
      const { validation, value } = this;
      if (!this.checkShouldValidate()) return this;

      const lengthType = typeof value === "string" ? "characters" : "entries";
      const text =
        message != null
          ? message
          : inclusive
          ? `Must be a maximum of ${amount} ${lengthType}`
          : `Must be less than ${amount} ${lengthType}`;

      if (value.length != null && (inclusive ? value.length > amount : value.length >= amount)) {
        validation.list.push({
          text,
          type: "error",
        });
      }

      this.setIfFailed();
      return this;
    },

    greaterThanOrEqualTo: (amount: number, message?: string) =>
      this.length.greaterThan(amount, true, message),

    lessThanOrEqualTo: (amount: number, message?: string) =>
      this.length.lessThan(amount, true, message),

    equalTo: (amount: number, message?: string) => {
      const { validation, value } = this;
      if (!this.checkShouldValidate()) return this;

      const lengthType = typeof value === "string" ? "characters" : "entries";

      if (value.length != null && value.length !== amount) {
        validation.list.push({
          text: message ?? `Must consist of ${amount} ${lengthType}`,
          type: "error",
        });
      }

      this.setIfFailed();
      return this;
    },
  };

  public regex = {
    matches: (regex: RegExp, message: string) => {
      const { validation, value } = this;
      if (!this.checkShouldValidate()) return this;

      if (typeof value !== "string" || !value.match(regex)) {
        validation.list.push({ text: message ?? "Does not match pattern", type: "error" });
      }

      this.setIfFailed();
      return this;
    },
    isEmail: (message?: string) =>
      this.regex.matches(emailRegex, message ?? "Must conform with example@email.com"),
  };

  public number = {
    greaterThan: (amount: number, inclusive?: boolean, message?: string) => {
      const { validation, value } = this;
      if (!this.checkShouldValidate()) return this;

      const text =
        message != null
          ? message
          : inclusive
          ? `Must be at least ${amount}`
          : `Must be greater than ${amount}`;

      if (typeof value === "number" && (inclusive ? value < amount : value <= amount)) {
        validation.list.push({
          text,
          type: "error",
        });
      }

      this.setIfFailed();
      return this;
    },

    lessThan: (amount: number, inclusive?: boolean, message?: string) => {
      const { validation, value } = this;
      if (!this.checkShouldValidate()) return this;

      const text =
        message != null
          ? message
          : inclusive
          ? `Must be a maximum of ${amount}`
          : `Must be less than ${amount}`;

      if (typeof value === "number" && (inclusive ? value > amount : value >= amount)) {
        validation.list.push({
          text,
          type: "error",
        });
      }

      this.setIfFailed();
      return this;
    },

    greaterThanOrEqualTo: (amount: number, message?: string) =>
      this.number.greaterThan(amount, true, message),

    lessThanOrEqualTo: (amount: number, message?: string) =>
      this.number.lessThan(amount, true, message),
  };

  public enum = {
    within: (_enum: any, message?: string) => {
      const { validation, value } = this;
      if (!this.checkShouldValidate()) return this;

      if (!enumContains(_enum, value)) {
        validation.list.push({
          text: message ?? "Invalid data",
          type: "error",
        });
      }

      this.setIfFailed();
      return this;
    },
  };

  public custom(validationFunction: (value: any) => string | void) {
    const { validation, value } = this;
    if (!this.checkShouldValidate()) return this;

    const error = validationFunction(value);
    if (error)
      validation.list.push({
        text: error,
        type: "error",
      });

    this.setIfFailed();
    return this;
  }

  public skipIf(pass: boolean) {
    this.shouldSkip = pass;
    return this;
  }

  public skipNextIf(pass: boolean) {
    this.shouldSkipNext = pass;
    return this;
  }

  private checkShouldSkipNext() {
    const { shouldSkipNext } = this;
    if (shouldSkipNext) {
      this.shouldSkipNext = false;
      return true;
    }
    return false;
  }

  private checkShouldSkip() {
    const { shouldSkip } = this;
    return shouldSkip;
  }

  private checkHasFailed() {
    const { failed } = this;
    return failed;
  }

  private checkShouldValidate() {
    return !this.checkShouldSkipNext() && !this.checkShouldSkip() && !this.checkHasFailed();
  }

  private setIfFailed() {
    this.failed = this.validation.list.length > 0;
  }

  public getValidation(): FormValidation {
    return this.validation;
  }

  public getValue(): any {
    return this.value;
  }

  public getFailed(): boolean {
    return this.failed;
  }
}
